import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Typography 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DepartmentDetail } from "../../components/DepartmentDetail/DepartmentDetail";
import PageContainer from '../../../../../components/layout/PageContainer';
import { useDepartments } from '../../hooks/useDepartments';

export const DepartmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const {
    selectedDepartment,
    isLoading,
    fetchDepartmentById,
    setSelectedDepartment
  } = useDepartments();

  useEffect(() => {
    const loadDepartment = async () => {
      if (!id) {
        navigate('..');
        return;
      }
      
      try {
        await fetchDepartmentById(id);
      } catch (error) {
        enqueueSnackbar('Failed to load department details', { variant: 'error' });
        console.error('Error fetching department:', error);
        navigate('..');
      }
    };

    loadDepartment();

    // Cleanup function to reset selected department when component unmounts
    return () => {
      setSelectedDepartment(null);
    };
  }, [id, navigate, enqueueSnackbar, fetchDepartmentById, setSelectedDepartment]);

  const handleEdit = () => {
    if (selectedDepartment) {
      navigate(`/settings/departments/${selectedDepartment.id}/edit`);
    }
  };


  const handleBack = () => {
    navigate('..');
  };

  if (isLoading) {
    return (
      <PageContainer title="Loading...">
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (!selectedDepartment) {
    return (
      <PageContainer title="Department Not Found">
        <Box p={3}>
          <Typography variant="h6" gutterBottom>
            Department not found
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Back to Departments
          </Button>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer title={selectedDepartment.name}>
      <DepartmentDetail
        department={selectedDepartment}
        onEdit={handleEdit}
        onBack={handleBack}
      />
    </PageContainer>
  );
};
